import { pick } from "lodash";
import { _internalApiGetRenderId, _internalApiGetRuntimeContext, _internalApiGetStoryboardInBootstrapData, getBrickPackages } from "./Runtime.js";
import { createScopedRuntimeContext, postAsyncRender, renderBrick } from "./Renderer.js";
import { RendererContext } from "./RendererContext.js";
import { resolveData } from "./data/resolveData.js";
import { asyncComputeRealValue } from "./compute/computeRealValue.js";
import { mountTree, unmountTree } from "./mount.js";
import { RenderTag } from "./enums.js";
import { isStrictMode } from "../isStrictMode.js";
import { customTemplates } from "../CustomTemplates.js";
import { registerAppI18n } from "./registerAppI18n.js";
import { getTplStateStore } from "./CustomTemplates/utils.js";
import { loadBricksImperatively, loadEditorsImperatively } from "@next-core/loader";
import { getMatchedRoute } from "./routeMatchedMap.js";
export const symbolForRootRuntimeContext = Symbol.for("root.runtimeContext");
export async function renderUseBrick(useBrick, data, errorBoundary) {
  var _useBrick$symbolForRo, _scopedRuntimeContext, _scopedRuntimeContext2, _brickNode;
  const [scopedRuntimeContext, tplStateStoreScope, formStateStoreScope] = createScopedRuntimeContext({
    ...((_useBrick$symbolForRo = useBrick[symbolForRootRuntimeContext]) !== null && _useBrick$symbolForRo !== void 0 ? _useBrick$symbolForRo : _internalApiGetRuntimeContext()),
    data,
    pendingPermissionsPreCheck: []
  });
  scopedRuntimeContext.inUseBrick = true;
  (_scopedRuntimeContext = scopedRuntimeContext.tplStateStoreMap) !== null && _scopedRuntimeContext !== void 0 ? _scopedRuntimeContext : scopedRuntimeContext.tplStateStoreMap = new Map();
  (_scopedRuntimeContext2 = scopedRuntimeContext.formStateStoreMap) !== null && _scopedRuntimeContext2 !== void 0 ? _scopedRuntimeContext2 : scopedRuntimeContext.formStateStoreMap = new Map();
  const rendererContext = new RendererContext("fragment");
  const renderRoot = {
    tag: RenderTag.ROOT,
    // Will set during `mountUseBrick`
    createPortal: null
  };
  const strict = isStrictMode();
  const output = await renderBrick(renderRoot, strict && !errorBoundary ? useBrick : {
    errorBoundary,
    ...useBrick
  }, scopedRuntimeContext, rendererContext, [], {});
  const scopedStores = [...tplStateStoreScope, ...formStateStoreScope];
  await postAsyncRender(output, scopedRuntimeContext, scopedStores);
  let brickNode;
  let currentNode = output.node;
  while (currentNode) {
    if (currentNode.tag === RenderTag.BRICK) {
      brickNode = currentNode;
      break;
    }
    currentNode = currentNode.child;
  }
  if ((_brickNode = brickNode) !== null && _brickNode !== void 0 && _brickNode.portal) {
    throw new Error("The root brick of useBrick cannot be a portal brick");
  }
  renderRoot.child = output.node;
  const tagName = brickNode ? brickNode.type : null;
  return {
    tagName,
    renderRoot,
    rendererContext,
    scopedStores
  };
}
export function mountUseBrick(_ref, element) {
  let {
    renderRoot,
    rendererContext,
    scopedStores
  } = _ref;
  let portal;
  renderRoot.createPortal = () => {
    const portalRoot = document.querySelector("#portal-mount-point");
    portal = document.createElement("div");
    portalRoot.appendChild(portal);
    return portal;
  };
  mountTree(renderRoot, element);
  rendererContext.dispatchOnMount();
  rendererContext.initializeScrollIntoView();
  rendererContext.initializeMediaChange();
  rendererContext.initializeMessageDispatcher();
  for (const store of scopedStores) {
    store.mountAsyncData();
  }
  return {
    portal
  };
}
export function unmountUseBrick(_ref2, mountResult) {
  let {
    rendererContext
  } = _ref2;
  // if (mountResult.mainBrick) {
  //   mountResult.mainBrick.unmount();
  // }
  if (mountResult.portal) {
    unmountTree(mountResult.portal);
    mountResult.portal.remove();
  }
  rendererContext.dispatchOnUnmount();
  rendererContext.dispose();
}
export function updateStoryboard(appId, storyboardPatch) {
  const storyboard = _internalApiGetStoryboardInBootstrapData(appId);
  Object.assign(storyboard, {
    ...storyboardPatch,
    meta: {
      // Keep runtime fields such as `injectMenus`
      ...storyboard.meta,
      ...storyboardPatch.meta
    },
    $$fulfilling: null,
    $$fulfilled: true,
    $$registerCustomTemplateProcessed: false
  });
  registerAppI18n(storyboard);
}
export function updateStoryboardByRoute(appId, newRoute) {
  const storyboard = _internalApiGetStoryboardInBootstrapData(appId);
  let matched = false;
  function matchRoute(route) {
    return route.path === newRoute.path && !route.exact === !newRoute.exact;
  }
  function replaceRoutes(routes) {
    routes.forEach((route, index) => {
      if (route.type === "routes") {
        replaceRoutes(route.routes);
      } else {
        if (matchRoute(route)) {
          matched = true;
          routes[index] = newRoute;
        } else if (Array.isArray(route.bricks)) {
          replaceBricks(route.bricks);
        }
      }
    });
  }
  function replaceBricks(bricks) {
    bricks.forEach(brick => {
      if (brick.slots) {
        for (const slotConf of Object.values(brick.slots)) {
          if (slotConf.type === "routes") {
            replaceRoutes(slotConf.routes);
          } else {
            replaceBricks(slotConf.bricks);
          }
        }
      }
    });
  }
  replaceRoutes(storyboard.routes);
  if (!matched) {
    storyboard.routes.unshift(newRoute);
  }
}
export function updateStoryboardByTemplate(appId, newTemplate, settings) {
  const tplName = `${appId}.${newTemplate.name}`;
  // customTemplateRegistry.delete(tplName);
  customTemplates.define(tplName, {
    bricks: newTemplate.bricks,
    proxy: newTemplate.proxy,
    state: newTemplate.state
  });
  updateTemplatePreviewSettings(appId, newTemplate.name, settings);
}
export function updateTemplatePreviewSettings(appId, templateId, settings) {
  _updatePreviewSettings(appId, `\${APP.homepage}/_dev_only_/template-preview/${templateId}`, [{
    brick: templateId,
    ...pick(settings, "properties", "events", "lifeCycle", "context", "slots", "children")
  }]);
}
function getSnippetPreviewPath(snippetId) {
  return `\${APP.homepage}/_dev_only_/snippet-preview/${snippetId}`;
}
export function updateStoryboardBySnippet(appId, snippetData) {
  var _snippetData$bricks;
  _updatePreviewSettings(appId, getSnippetPreviewPath(snippetData.snippetId), (_snippetData$bricks = snippetData.bricks) !== null && _snippetData$bricks !== void 0 && _snippetData$bricks.length ? snippetData.bricks : [{
    brick: "span"
  }], snippetData.context);
}
export const updateSnippetPreviewSettings = updateStoryboardBySnippet;
function _updatePreviewSettings(appId, path, bricks, context) {
  const {
    routes
  } = _internalApiGetStoryboardInBootstrapData(appId);
  const previewRouteIndex = routes.findIndex(route => route.path === path);
  const newPreviewRoute = {
    path,
    bricks,
    context,
    menu: false,
    exact: true
  };
  if (previewRouteIndex === -1) {
    routes.unshift(newPreviewRoute);
  } else {
    routes.splice(previewRouteIndex, 1, newPreviewRoute);
  }
}
export function getContextValue(name, _ref3) {
  let {
    tplStateStoreId
  } = _ref3;
  const runtimeContext = _internalApiGetRuntimeContext();
  if (tplStateStoreId) {
    const tplStateStore = getTplStateStore({
      ...runtimeContext,
      tplStateStoreId
    }, "STATE");
    return tplStateStore.getValue(name);
  }
  return runtimeContext.ctxStore.getValue(name);
}
export function getAllContextValues(_ref4) {
  let {
    tplStateStoreId
  } = _ref4;
  const runtimeContext = _internalApiGetRuntimeContext();
  if (tplStateStoreId) {
    const tplStateStore = getTplStateStore({
      ...runtimeContext,
      tplStateStoreId
    }, "STATE");
    return tplStateStore.getAllValues();
  }
  return runtimeContext.ctxStore.getAllValues();
}
export { getBrickPackages };
export function getBrickPackagesById(id) {
  return getBrickPackages().find(pkg => pkg.id ? pkg.id === id : pkg.filePath.startsWith(`${id}/`));
}

/**
 * Support loading v3 bricks only.
 */
export function loadBricks(bricks) {
  return loadBricksImperatively(bricks, getBrickPackages());
}

/**
 * Try the brick packages passed in, before using from bootstrap.
 */
export function loadEditors(editors, brickPackages) {
  return loadEditorsImperatively(editors, brickPackages !== null && brickPackages !== void 0 ? brickPackages : getBrickPackages());
}
export function getRenderId() {
  return _internalApiGetRenderId();
}
export async function getAddedContracts(storyboardPatch, _ref5) {
  let {
    appId,
    updateStoryboardType,
    collectUsedContracts
  } = _ref5;
  const storyboard = _internalApiGetStoryboardInBootstrapData(appId);
  let updatedStoryboard;

  // 拿到更新部分的 storyboard 配置，然后扫描一遍，找到新增的 contracts
  if (updateStoryboardType === "route") {
    updatedStoryboard = {
      routes: [storyboardPatch]
    };
  } else if (updateStoryboardType === "template") {
    updatedStoryboard = {
      meta: {
        customTemplates: [storyboardPatch]
      }
    };
  } else if (updateStoryboardType === "snippet") {
    var _storyboard$routes;
    // snippet 是放在挂载 route 里预览，通过 previewPath 拿到当前修改 route
    const snippetPreviewPath = getSnippetPreviewPath(storyboardPatch.snippetId);
    const currentRoute = storyboard === null || storyboard === void 0 || (_storyboard$routes = storyboard.routes) === null || _storyboard$routes === void 0 ? void 0 : _storyboard$routes.find(route => route.path === snippetPreviewPath);
    updatedStoryboard = {
      routes: [currentRoute]
    };
  }
  const addedContracts = [];
  if (updatedStoryboard && collectUsedContracts) {
    const contractApis = await collectUsedContracts(updatedStoryboard);
    contractApis.forEach(api => {
      var _storyboard$meta;
      const [_, namespaceId, name] = api.match(/(.*)@(.*):\d\.\d\.\d/);
      if (!(storyboard !== null && storyboard !== void 0 && (_storyboard$meta = storyboard.meta) !== null && _storyboard$meta !== void 0 && (_storyboard$meta = _storyboard$meta.contracts) !== null && _storyboard$meta !== void 0 && _storyboard$meta.some(contract => contract.namespaceId === namespaceId && contract.name === name))) {
        addedContracts.push(api);
      }
    });
  }
  return addedContracts;
}
export async function debugDataValue(debugData, _ref6) {
  let {
    tplStateStoreId,
    routeId
  } = _ref6;
  const runtimeContext = {
    ..._internalApiGetRuntimeContext(),
    tplStateStoreId,
    match: getMatchedRoute(routeId)
  };
  if (debugData.resolve) {
    return resolveData(debugData.resolve, runtimeContext, {
      cache: "reload"
    });
  }
  return asyncComputeRealValue(debugData.value, runtimeContext);
}
export function getLegalRuntimeValue(options) {
  var _runtimeContext$overr;
  const runtimeContext = _internalApiGetRuntimeContext();
  return {
    app: (_runtimeContext$overr = runtimeContext === null || runtimeContext === void 0 ? void 0 : runtimeContext.overrideApp) !== null && _runtimeContext$overr !== void 0 ? _runtimeContext$overr : runtimeContext === null || runtimeContext === void 0 ? void 0 : runtimeContext.app,
    location: pick(location, ["href", "origin", "hostname", "host"]),
    ...pick(runtimeContext, ["query", "sys"]),
    match: getMatchedRoute(options === null || options === void 0 ? void 0 : options.routeId)
  };
}
export { setRealTimeDataInspectRoot, addRealTimeDataInspectHook } from "./data/realTimeDataInspect.js";
export { legacyDoTransform, legacyTransformProperties, legacyTransformIntermediateData } from "./legacy_transform.js";
//# sourceMappingURL=secret_internals.js.map